import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { app } from '@/lib/firebase';

const functionsOrigin = (): string => {
  const projectId =
    (app as { options?: { projectId?: string } } | undefined)?.options?.projectId ||
    (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined) || 'studio-8849194767-f8721';
  return `https://us-central1-${projectId}.cloudfunctions.net`;
};

type Category = { id: string; name: string; color?: string | null };
type Character = { summary: string; voice: string; audience: string; themes: string[]; signature: string[]; updatedAt?: string | null };
type Channel = {
  channelId: string; title?: string; handle?: string; thumbnailUrl?: string; color?: string; description?: string; videoCount?: number;
  categories?: Category[]; character?: Character | null; subscriberCount?: number | null; country?: string | null; bannerUrl?: string | null;
  ownerVerified?: boolean; curatorManaged?: boolean;
};

function TrustBadge({ ch, small = false }: { ch: Channel; small?: boolean }) {
  if (!ch.ownerVerified) return null;
  const label = ch.curatorManaged ? 'Owner verified · curated' : 'YouTube owner ✓';
  return (
    <span className={`inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-400/15 font-semibold uppercase tracking-wide text-emerald-200 ${small ? 'px-1.5 py-0 text-[9px]' : 'px-2 py-0.5 text-[10px]'}`}>
      {label}
    </span>
  );
}
type Ev = { id: string; title?: string; summary?: string; eventDateKey?: string; datePrecision?: string; confidence?: number; sourceTimestampUrl?: string; videoId?: string; sourceTimestampSec?: number; categoryIds?: string[] };

const compact = (n: number) => new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
const clock = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};

function Badge({ c }: { c: Category }) {
  const color = c.color || '#a78bfa';
  return (
    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium" style={{ borderColor: `${color}66`, backgroundColor: `${color}1f`, color }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />{c.name}
    </span>
  );
}

// Thumbnail of the source video with the clip's timestamp; click swaps in an embed that starts there.
function Clip({ videoId, startSec, title }: { videoId: string; startSec: number; title: string }) {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <div className="mt-2 aspect-video w-full max-w-md overflow-hidden rounded-lg border border-white/10 bg-black">
        <iframe className="h-full w-full" src={`https://www.youtube-nocookie.com/embed/${videoId}?start=${startSec}&autoplay=1&rel=0&modestbranding=1`} title={title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  return (
    <button type="button" onClick={() => setPlaying(true)} className="group relative mt-2 block aspect-video w-full max-w-md overflow-hidden rounded-lg border border-white/10 bg-black text-left" aria-label={`Play clip at ${clock(startSec)}`}>
      <img src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`} alt="" className="h-full w-full object-cover opacity-80 transition group-hover:opacity-100" />
      <span className="absolute inset-0 flex items-center justify-center"><span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-black transition group-hover:scale-105">▶</span></span>
      <span className="absolute bottom-1.5 right-1.5 rounded bg-black/75 px-1.5 py-0.5 text-[11px] font-semibold">▶ {clock(startSec)}</span>
    </button>
  );
}

const fmtDate = (key = '', precision = ''): string => {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (d) return new Date(Date.UTC(+d[1], +d[2] - 1, +d[3])).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, 1)).toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  return key || precision || '—';
};

export default function ChannelProfile() {
  const { channelId = '' } = useParams();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [events, setEvents] = useState<Ev[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      fetch(`${functionsOrigin()}/listPublicChannels`).then((r) => r.json()).catch(() => ({ channels: [] })),
      fetch(`${functionsOrigin()}/getTimelineByDate?channelId=${encodeURIComponent(channelId)}`).then((r) => r.json()).catch(() => ({ events: [] })),
    ]).then(([chJson, evJson]) => {
      if (!alive) return;
      setChannel((chJson.channels || []).find((c: Channel) => c.channelId === channelId) || { channelId });
      setEvents(((evJson.events || []) as Ev[]).slice().sort((a, b) => String(b.eventDateKey).localeCompare(String(a.eventDateKey))));
      setLoading(false);
    });
    return () => { alive = false; };
  }, [channelId]);

  const color = channel?.color || '#f59e0b';
  const feeds = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://thirteen.happyapps.dev';
    const https = `${origin}/channel-feed/${channelId}`;
    const webcal = https.replace(/^https?:/, 'webcal:');
    return { https, webcal, gcal: `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}` };
  }, [channelId]);

  const copyIcs = async () => { try { await navigator.clipboard.writeText(feeds.https); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ } };

  return (
    <div className="min-h-screen bg-[#050416] text-white">
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-10">
        <Link to="/dashboard/thirteen" className="mb-6 inline-block rounded-md border border-white/15 px-3 py-1.5 text-sm hover:bg-white/10">← Calendar</Link>

        {/* header */}
        <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5" style={{ boxShadow: `inset 4px 0 0 ${color}` }}>
          {channel?.thumbnailUrl ? (
            <img src={channel.thumbnailUrl} alt="" className="h-16 w-16 rounded-full object-cover ring-2 ring-white/10" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full text-2xl font-bold" style={{ backgroundColor: color, color: '#111' }}>{(channel?.title || '?').slice(0, 1)}</div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{channel?.title || channelId}</h1>
              {channel ? <TrustBadge ch={channel} /> : null}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/60">
              {channel?.handle ? <a href={`https://youtube.com/${channel.handle.startsWith('@') ? channel.handle : '@' + channel.handle}`} target="_blank" rel="noopener noreferrer" className="hover:text-white">{channel.handle}</a> : null}
              {typeof channel?.subscriberCount === 'number' ? <span>{compact(channel.subscriberCount)} subscribers</span> : null}
              <span>{channel?.videoCount ?? 0} videos</span>
              <span>{events.length} timeline events</span>
              {channel?.country ? <span>{channel.country}</span> : null}
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} /> event colour</span>
            </div>
            {channel?.categories?.length ? (
              <div className="mt-2 flex flex-wrap gap-1.5">{channel.categories.map((c) => <Badge key={c.id} c={c} />)}</div>
            ) : null}
          </div>
        </div>

        {/* character */}
        {channel?.character?.summary ? (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-white/45">Channel character</div>
            <p className="mt-2 text-sm leading-6 text-white/85">{channel.character.summary}</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {channel.character.voice ? <div><div className="text-[11px] uppercase tracking-wider text-white/40">Voice</div><div className="mt-0.5 text-sm text-white/75">{channel.character.voice}</div></div> : null}
              {channel.character.audience ? <div><div className="text-[11px] uppercase tracking-wider text-white/40">For</div><div className="mt-0.5 text-sm text-white/75">{channel.character.audience}</div></div> : null}
            </div>
            {channel.character.themes?.length ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {channel.character.themes.map((t) => <span key={t} className="rounded-full border px-2.5 py-0.5 text-xs" style={{ borderColor: `${color}66`, color }}>{t}</span>)}
              </div>
            ) : null}
            {channel.character.signature?.length ? (
              <ul className="mt-3 list-disc space-y-0.5 pl-5 text-sm text-white/60">{channel.character.signature.map((s) => <li key={s}>{s}</li>)}</ul>
            ) : null}
          </div>
        ) : channel?.description ? (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm leading-6 text-white/70 whitespace-pre-wrap">{channel.description}</div>
        ) : null}

        {/* subscribe */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <div className="text-sm font-semibold text-white/90">Subscribe in your calendar app</div>
          <p className="mt-1 text-xs text-white/50">A live, read-only calendar of this channel's timeline. It auto-syncs to Apple Calendar, Google Calendar, and Outlook.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href={feeds.webcal} className="rounded-md px-3 py-1.5 text-sm font-medium text-black" style={{ backgroundColor: color }}>Subscribe (Apple / Outlook)</a>
            <a href={feeds.gcal} target="_blank" rel="noopener noreferrer" className="rounded-md border border-white/15 px-3 py-1.5 text-sm hover:bg-white/10">Add to Google Calendar</a>
            <button type="button" onClick={copyIcs} className="rounded-md border border-white/15 px-3 py-1.5 text-sm hover:bg-white/10">{copied ? '✓ Copied' : 'Copy ICS URL'}</button>
          </div>
        </div>

        {/* events */}
        <div className="mt-6">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/45">Timeline ({events.length})</div>
          {loading ? (
            <div className="text-sm text-white/50">Loading…</div>
          ) : events.length === 0 ? (
            <div className="text-sm text-white/50">No timeline events yet.</div>
          ) : (
            <div className="space-y-1.5">
              {events.map((e) => (
                <div key={e.id} className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium">{e.title}</span>
                      {channel ? <TrustBadge ch={channel} small /> : null}
                      {(e.categoryIds ?? []).map((id) => channel?.categories?.find((c) => c.id === id)).filter((c): c is Category => Boolean(c)).map((c) => <Badge key={c.id} c={c} />)}
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-white/45">{fmtDate(e.eventDateKey, e.datePrecision)}</span>
                  </div>
                  {e.summary ? <div className="mt-0.5 text-sm text-white/55">{e.summary}</div> : null}
                  {e.videoId ? (
                    <Clip videoId={e.videoId} startSec={Math.max(0, Math.floor(e.sourceTimestampSec ?? Number((e.sourceTimestampUrl ?? '').match(/[?&]t=(\d+)/)?.[1] ?? 0)))} title={e.title ?? 'Source clip'} />
                  ) : e.sourceTimestampUrl ? (
                    <a href={e.sourceTimestampUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-xs text-white/45 hover:text-white/80">▶ Watch source</a>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
